import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useQueryState, parseAsInteger, parseAsString } from 'nuqs';
import {
  Link2,
  Search,
  Copy,
  QrCode,
  ExternalLink,
  Trash2,
  BarChart3,
  MousePointerClick,
  Download,
  Upload,
  Check,
  X,
  Bot,
  Pencil,
  Share2,
  ChevronLeft,
  ChevronRight,
  FlaskConical,
  Tag,
} from 'lucide-react';
import { toast } from 'sonner';
import { useUIStore } from '@/client/stores/ui-store';
import { formatNumber, formatRelativeTime, copyToClipboard } from '@/client/lib/utils';
import { apiFetch } from '@/client/lib/api';
import {
  SortSelector,
  type SortField,
  type SortDirection,
} from '@/client/components/ui/sort-selector';
import { OnboardingEmptyState, SearchEmptyState } from '@/client/components/ui/empty-state';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent } from '@/client/components/ui/card';
import { Badge } from '@/client/components/ui/badge';
import { Skeleton } from '@/client/components/ui/skeleton';
import { Input } from '@/client/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/client/components/ui/tooltip';

// Helper to truncate long URLs for display
function truncateUrl(url: string, maxLength = 40) {
  if (url.length <= maxLength) return url;
  return `${url.substring(0, maxLength - 3)}…`;
}

// Type for link data from API
interface LinkData {
  id: string;
  shortCode: string;
  shortUrl: string;
  destinationUrl: string;
  title?: string | null;
  tags?: string[];
  folderId?: string | null;
  validUntil?: string | null;
  maxVisits?: number | null;
  isActive?: boolean;
  totalClicks?: number;
  botClicks?: number;
  testClicks?: number;
  createdAt: string;
}

export function LinksPage() {
  const navigate = useNavigate();
  const { openImportLinksModal, openQrModal, openDeleteConfirmation } = useUIStore();

  // URL-synced state with nuqs - enables bookmarkable/shareable filter URLs
  const [searchQuery, setSearchQuery] = useQueryState('q', parseAsString.withDefault(''));
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [orderBy, setOrderBy] = useQueryState('sort', parseAsString.withDefault('createdAt'));
  const [orderDir, setOrderDir] = useQueryState('order', parseAsString.withDefault('desc'));

  // Combined setter for sort to match SortSelector API
  const setSort = (field: SortField, direction: SortDirection) => {
    setOrderBy(field);
    setOrderDir(direction);
  };

  const handleExport = async () => {
    try {
      const response = await apiFetch('/api/links/export');
      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `links-export-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Links exported successfully!');
    } catch {
      toast.error('Failed to export links');
    }
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['links', { searchQuery, page, orderBy, orderDir }],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        itemsPerPage: '20',
        orderBy,
        orderDir,
      });
      if (searchQuery) params.set('searchTerm', searchQuery);

      const response = await apiFetch(`/api/links?${params}`);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Links API error:', response.status, errorText);
        throw new Error(`Failed to fetch links: ${response.status}`);
      }
      return response.json();
    },
  });

  const handleCopyLink = async (shortUrl: string, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    await copyToClipboard(shortUrl);
    toast.success('Link copied to clipboard!');
  };

  // Calculate total stats from visible links (memoized to avoid recalculation on every render)
  const totalClicks = useMemo(() => {
    return (
      data?.data?.reduce((sum: number, link: LinkData) => sum + (link.totalClicks ?? 0), 0) ?? 0
    );
  }, [data?.data]);

  return (
    <div className="space-y-6 stagger-children relative">
      {/* Decorative background gradient */}
      <div className="absolute inset-0 overflow-hidden opacity-30 pointer-events-none">
        <div className="absolute -top-1/2 -right-1/4 w-[800px] h-[800px] rounded-full bg-gradient-to-br from-amber-500/20 via-orange-500/10 to-transparent blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/4 w-[600px] h-[600px] rounded-full bg-gradient-to-tr from-orange-500/20 via-amber-500/10 to-transparent blur-3xl" />
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 relative">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
            <div className="size-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <Link2 className="size-5 text-white" aria-hidden="true" />
            </div>
            Links
          </h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">
            Shorten and track your URLs
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={handleExport}
            className="hidden sm:inline-flex rounded-xl"
          >
            <Download aria-hidden="true" />
            Export
          </Button>
          <Button
            variant="outline"
            onClick={openImportLinksModal}
            className="hidden sm:inline-flex rounded-xl"
          >
            <Upload aria-hidden="true" />
            Import
          </Button>
          <Button
            onClick={() => navigate('/links/new')}
            size="lg"
            className="rounded-xl glow gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
          >
            <Link2 className="size-5" aria-hidden="true" />
            Create Link
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      {data?.pagination?.totalItems > 0 && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
            <CardContent className="flex items-center gap-4 p-4">
              <div className="size-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
                <Link2 className="size-6 text-white" aria-hidden="true" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground font-medium">Total Links</p>
                <p className="text-2xl font-bold tabular-nums">
                  {formatNumber(data?.pagination?.totalItems ?? 0)}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30">
            <CardContent className="flex items-center gap-4 p-4">
              <div className="size-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/20">
                <MousePointerClick className="size-6 text-white" aria-hidden="true" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground font-medium">Total Clicks</p>
                <p className="text-2xl font-bold tabular-nums">{formatNumber(totalClicks)}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30">
            <CardContent className="flex items-center gap-4 p-4">
              <div className="size-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
                <BarChart3 className="size-6 text-white" aria-hidden="true" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground font-medium">Avg. Clicks</p>
                <p className="text-2xl font-bold tabular-nums">
                  {data?.pagination?.totalItems > 0
                    ? formatNumber(Math.round(totalClicks / (data?.data?.length || 1)))
                    : '0'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="text"
            name="search"
            autoComplete="off"
            spellCheck={false}
            placeholder="Search by URL or short code…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 h-12 rounded-xl"
          />
        </div>
        <SortSelector
          orderBy={orderBy as SortField}
          orderDir={orderDir as SortDirection}
          onChange={setSort}
        />
      </div>

      {/* Links List */}
      <Card className="rounded-2xl overflow-hidden py-0">
        {error ? (
          <CardContent className="p-12 text-center">
            <div className="size-24 rounded-3xl bg-destructive/10 flex items-center justify-center mx-auto mb-6">
              <Link2 className="size-12 text-destructive/50" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-destructive">Error Loading Links</h3>
            <p className="text-muted-foreground mb-4">{error.message}</p>
            <p className="text-sm text-muted-foreground">
              Check the browser console for more details.
            </p>
          </CardContent>
        ) : isLoading ? (
          <CardContent className="p-6 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 p-4">
                <Skeleton className="size-14 rounded-xl" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-1/3" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
                <Skeleton className="w-20 h-12 rounded-lg" />
              </div>
            ))}
          </CardContent>
        ) : data?.data?.length === 0 ? (
          searchQuery ? (
            <SearchEmptyState
              searchQuery={searchQuery}
              onClear={() => setSearchQuery('')}
              entityName="links"
            />
          ) : (
            <OnboardingEmptyState
              icon={Link2}
              title="Create your first link"
              steps={[
                { icon: Link2, text: 'Paste any long URL to shorten it' },
                { icon: Share2, text: 'Share your clean, memorable link anywhere' },
                { icon: BarChart3, text: 'Track clicks, locations, and devices' },
              ]}
              action={{
                label: 'Create your first link',
                onClick: () => navigate('/links/new'),
                icon: Link2,
              }}
            />
          )
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block">
              <Table>
                <TableHeader className="bg-muted/50">
                  <TableRow>
                    <TableHead className="px-4">Link</TableHead>
                    <TableHead className="px-4">Clicks</TableHead>
                    <TableHead className="px-4">
                      <span className="flex items-center gap-1">
                        <Bot className="size-3.5" aria-hidden="true" />
                        Bots
                      </span>
                    </TableHead>
                    <TableHead className="px-4">
                      <span className="flex items-center gap-1">
                        <FlaskConical className="size-3.5" aria-hidden="true" />
                        Tests
                      </span>
                    </TableHead>
                    <TableHead className="px-4">Status</TableHead>
                    <TableHead className="px-4">Created</TableHead>
                    <TableHead className="px-4 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.data?.map((link: LinkData) => (
                    <TableRow key={link.shortCode} className="group">
                      <TableCell className="px-4 py-3 max-w-md">
                        <div className="space-y-1">
                          {/* Title (if present) */}
                          {link.title && (
                            <div className="font-medium text-sm truncate" title={link.title}>
                              {link.title}
                            </div>
                          )}

                          {/* Short link with copy button */}
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/links/${link.shortCode}`}
                              className="font-mono text-sm text-primary hover:underline"
                            >
                              arml.ink/{link.shortCode}
                            </Link>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={(e) => handleCopyLink(link.shortUrl, e)}
                                  className="opacity-0 group-hover:opacity-100 transition-all"
                                  aria-label="Copy link to clipboard"
                                >
                                  <Copy className="size-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Copy link</TooltipContent>
                            </Tooltip>
                          </div>

                          {/* Destination URL */}
                          <a
                            href={link.destinationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-muted-foreground hover:text-foreground truncate block"
                            title={link.destinationUrl}
                          >
                            {truncateUrl(link.destinationUrl, 50)}
                          </a>

                          {/* Tags */}
                          {link.tags && link.tags.length > 0 && (
                            <div className="flex items-center gap-1 flex-wrap pt-1">
                              <Tag className="size-3 text-muted-foreground" aria-hidden="true" />
                              {link.tags.slice(0, 3).map((tag) => (
                                <Badge
                                  key={tag}
                                  variant="secondary"
                                  className="text-[10px] px-1.5 py-0 font-normal"
                                >
                                  {tag}
                                </Badge>
                              ))}
                              {link.tags.length > 3 && (
                                <span className="text-[10px] text-muted-foreground">
                                  +{link.tags.length - 3} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <span className="font-medium tabular-nums">
                          {formatNumber(link.totalClicks ?? 0)}
                        </span>
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        {(link.botClicks ?? 0) > 0 ? (
                          <Badge variant="destructive-subtle" className="gap-1">
                            <Bot className="size-3" aria-hidden="true" />
                            {formatNumber(link.botClicks ?? 0)}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        {(link.testClicks ?? 0) > 0 ? (
                          <Badge
                            variant="secondary"
                            className="gap-1 bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
                          >
                            <FlaskConical className="size-3" aria-hidden="true" />
                            {formatNumber(link.testClicks ?? 0)}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <Badge variant={link.isActive !== false ? 'success-subtle' : 'secondary'}>
                          {link.isActive !== false ? (
                            <>
                              <Check className="size-3" aria-hidden="true" /> Active
                            </>
                          ) : (
                            <>
                              <X className="size-3" aria-hidden="true" /> Inactive
                            </>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-sm text-muted-foreground">
                        {formatRelativeTime(link.createdAt)}
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => navigate(`/links/${link.shortCode}/edit`)}
                                aria-label="Edit link"
                              >
                                <Pencil className="size-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Edit link</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => openQrModal(link.shortCode, link.shortUrl)}
                                aria-label="Generate QR code"
                              >
                                <QrCode className="size-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>QR Code</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon-sm" asChild>
                                <a
                                  href={link.shortUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label="Open link in new tab"
                                >
                                  <ExternalLink className="size-4" />
                                </a>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Open link</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon-sm" asChild>
                                <Link to={`/links/${link.shortCode}`} aria-label="View analytics">
                                  <BarChart3 className="size-4" />
                                </Link>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>View analytics</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() =>
                                  openDeleteConfirmation('link', link.shortCode, link.shortCode)
                                }
                                className="hover:bg-destructive/10 hover:text-destructive"
                                aria-label="Delete link"
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete</TooltipContent>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-border">
              {data?.data?.map((link: LinkData) => (
                <Link
                  key={link.shortCode}
                  to={`/links/${link.shortCode}`}
                  className="block p-4 hover:bg-muted/30 transition-colors"
                >
                  {/* Title (if present) */}
                  {link.title && <p className="font-medium text-sm truncate mb-1">{link.title}</p>}

                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-sm text-primary">
                      arml.ink/{link.shortCode}
                    </span>
                    <Badge variant={link.isActive !== false ? 'success-subtle' : 'secondary'}>
                      {link.isActive !== false ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>

                  <p className="text-xs text-muted-foreground truncate mb-2">
                    {link.destinationUrl}
                  </p>

                  {/* Tags */}
                  {link.tags && link.tags.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap mb-2">
                      <Tag className="size-3 text-muted-foreground" aria-hidden="true" />
                      {link.tags.slice(0, 2).map((tag) => (
                        <Badge
                          key={tag}
                          variant="secondary"
                          className="text-[10px] px-1.5 py-0 font-normal"
                        >
                          {tag}
                        </Badge>
                      ))}
                      {link.tags.length > 2 && (
                        <span className="text-[10px] text-muted-foreground">
                          +{link.tags.length - 2}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{formatRelativeTime(link.createdAt)}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {formatNumber(link.totalClicks ?? 0)} clicks
                      </span>
                      {(link.testClicks ?? 0) > 0 && (
                        <Badge
                          variant="secondary"
                          className="text-[10px] px-1.5 py-0 bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
                        >
                          <FlaskConical className="size-2.5" aria-hidden="true" />
                          {formatNumber(link.testClicks ?? 0)}
                        </Badge>
                      )}
                      {(link.botClicks ?? 0) > 0 && (
                        <Badge variant="destructive-subtle" className="text-[10px] px-1.5 py-0">
                          <Bot className="size-2.5" aria-hidden="true" />
                          {formatNumber(link.botClicks ?? 0)}
                        </Badge>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {data?.pagination && data.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t bg-muted/20">
                <p className="text-sm text-muted-foreground">
                  Showing <span className="font-medium text-foreground">{(page - 1) * 20 + 1}</span>{' '}
                  to{' '}
                  <span className="font-medium text-foreground">
                    {Math.min(page * 20, data.pagination.totalItems)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium text-foreground">{data.pagination.totalItems}</span>{' '}
                  links
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="rounded-lg"
                  >
                    <ChevronLeft className="size-4" aria-hidden="true" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page === data.pagination.totalPages}
                    className="rounded-lg"
                  >
                    Next
                    <ChevronRight className="size-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
